// src/utils/admin/ads/adsApi.js

import axios from 'axios';
import { auth } from '../../firebase/init'; // Assuming you have firebase initialized like in permissionsApi.js

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://safemcc-portal-gateway-v2-bvn24355.ew.gateway.dev' //'https://safemcc-portal-gateway-bvn24355.ew.gateway.dev';
//console.log('[adsApi.js] Initial API_BASE_URL:', API_BASE_URL);

// Helper for getting Firebase auth token (similar to permissionsApi.js)
const getAuthTokenForAds = async () => {
    try {
        if (!auth.currentUser) {
            //console.log('[adsApi.js] getAuthTokenForAds: currentUser is null, waiting briefly...');
            await new Promise(resolve => setTimeout(resolve, 1500)); // Wait 1.5s, then check again
            if (!auth.currentUser) {
                console.error('[adsApi.js] getAuthTokenForAds: No authenticated user after delay.');
                throw new Error('No authenticated user for Ads API');
            }
             console.log('[adsApi.js] getAuthTokenForAds: currentUser found after delay.');
        }
        const token = await auth.currentUser.getIdToken(true);
        // No need to log anything about the token for security reasons
        return token;
    } catch (error) {
        console.error('[adsApi.js] Failed to get auth token for Ads');
        throw error;
    }
};

const createAxiosConfig = async (options = {}) => {
    let token;
    try {
        token = await getAuthTokenForAds();
        return {
            ...options,
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
                ...(options.headers || {})
            },
            timeout: 60000
        };
    } catch (error) {
        console.warn('[adsApi.js] createAxiosConfig: Could not generate auth token for request, proceeding without Authorization header.');
        return {
            ...options,
            headers: { // Still set Content-Type
                'Content-Type': 'application/json',
                ...(options.headers || {})
            },
            timeout: 60000
        };
    }
};

/**
 * Fetches the list of users with access from the BigQuery table via the backend API.
 * @returns {Promise<Array>} Promise resolving to an array of user access objects.
 */
export const fetchUsersFromApi = async (forceRefresh = false) => {
    // NOTE: This now uses the BigQuery-backed ads_users table via the new endpoint.
    // We also normalize the response to preserve the existing frontend field names
    // (email_address, access_creation_date_time) so modify/remove flows remain unchanged.
    const endpointPath = '/api/google-ads/users';
    const fullUrl = `${API_BASE_URL}${endpointPath}?_t=${new Date().getTime()}${forceRefresh ? '&force=true' : ''}`;
    //console.log(`[adsApi.js] fetchUsersFromApi: Requesting URL: ${fullUrl}`);
    try {
        const config = await createAxiosConfig();
        const response = await axios.get(fullUrl, config);
        //console.log(`[adsApi.js] fetchUsersFromApi: Response for ${fullUrl} - Status: ${response.status}`);
        const rows = response.data;
        if (!Array.isArray(rows)) return rows;

        // Normalize to existing frontend keys + keep new keys (account_type, manager)
        return rows.map((r) => ({
            ...r,
            account_type: r.account_type ?? r.accountType ?? '',
            manager: r.manager ?? '',
            customer_id: r.customer_id ?? r.customerId,
            customer_name: r.customer_name ?? r.customerName ?? r.account_name ?? r.accountName,
            email_address: r.user_email ?? r.userEmail ?? r.email_address ?? r.emailAddress,
            user_id: r.user_id ?? r.userId,
            access_role: r.access_role ?? r.accessRole,
            access_creation_date_time: r.access_creation_datetime ?? r.access_creation_date_time ?? r.accessCreationDatetime ?? r.accessCreationDateTime,
            expiration_date: r.expiration_date ?? r.expirationDate ?? null
        }));
    } catch (error) {
        console.error(`[adsApi.js] Error fetching Google Ads users from ${fullUrl}: Status: ${error.response?.status}, Code: ${error.code}, Message: ${error.message}, Data:`, error.response?.data ? JSON.stringify(error.response.data) : 'N/A');
        throw error;
    }
};


/**
 * Fetches the list of pending invitations for a specific customer ID via the backend API.
 * @param {string} customerId - The Google Ads Customer ID.
 * @param {string} requestingUser - The email of the user making the request.
 * @returns {Promise<Array>} Promise resolving to an array of invitation objects.
 */
export const fetchInvitationsFromApi = async (customerId, requestingUser, forceRefresh = false) => {
    const endpointPath = '/api/google-ads/invitations';
    const fullUrl = `${API_BASE_URL}${endpointPath}?customerId=${customerId}&requestingUser=${requestingUser}&_t=${new Date().getTime()}${forceRefresh ? '&force=true' : ''}`;
    //console.log(`[adsApi.js] fetchInvitationsFromApi: Requesting URL: ${fullUrl}`);
    try {
        const config = await createAxiosConfig();
        const response = await axios.get(fullUrl, config);
        //console.log(`[adsApi.js] fetchInvitationsFromApi: Response for ${fullUrl} - Status: ${response.status}`);
        
        // Handle the specific response structure from the API
        // Log the actual structure to help debugging
        //console.log(`[adsApi.js] fetchInvitationsFromApi: Response structure:`, 
        //   JSON.stringify(response.data).substring(0, 500) + (JSON.stringify(response.data).length > 500 ? '...' : ''));
        
        // Check for nested path details.invitations which contains the actual array
        if (response.data && 
            response.data.details && 
            response.data.details.invitations && 
            Array.isArray(response.data.details.invitations)) {
            
            //console.log(`[adsApi.js] fetchInvitationsFromApi: Found invitations array in response.data.details.invitations with ${response.data.details.invitations.length} items`);
            return response.data.details.invitations;
        }
        
        // Handle other possible response structures
        if (!Array.isArray(response.data)) {
            //console.log(`[adsApi.js] fetchInvitationsFromApi: Response is not an array, actual type:`, typeof response.data);
            
            // Check for response.data.data.details.invitations (extra nesting)
            if (response.data && 
                response.data.data && 
                response.data.data.details && 
                response.data.data.details.invitations && 
                Array.isArray(response.data.data.details.invitations)) {
                
                //console.log(`[adsApi.js] fetchInvitationsFromApi: Found invitations array in response.data.data.details.invitations with ${response.data.data.details.invitations.length} items`);
                return response.data.data.details.invitations;
            }
            
            // Check if response.data has a data property that's an array
            if (response.data && Array.isArray(response.data.data)) {
                //console.log(`[adsApi.js] fetchInvitationsFromApi: Found array in response.data.data with ${response.data.data.length} items`);
                return response.data.data;
            } 
            
            // Try to find any array nested in the response
            let invitationsArray = null;
            
            // Function to recursively search for an array named "invitations" up to depth 4
            const findInvitationsArray = (obj, currentDepth = 0, maxDepth = 4) => {
                if (!obj || typeof obj !== 'object' || currentDepth > maxDepth) return null;
                
                // Check if obj.invitations is an array
                if (obj.invitations && Array.isArray(obj.invitations)) {
                    return obj.invitations;
                }
                
                // Recursively check all object properties
                for (const key in obj) {
                    if (typeof obj[key] === 'object') {
                        const result = findInvitationsArray(obj[key], currentDepth + 1, maxDepth);
                        if (result) return result;
                    }
                }
                
                return null;
            };
            
            invitationsArray = findInvitationsArray(response.data);
            if (invitationsArray) {
                //console.log(`[adsApi.js] fetchInvitationsFromApi: Found invitations array through recursive search with ${invitationsArray.length} items`);
                return invitationsArray;
            }
            
            // If we can't find a specific invitations array, try to find any array
            if (response.data && typeof response.data === 'object') {
                const possibleArrays = Object.entries(response.data)
                    .filter(([_, value]) => Array.isArray(value))
                    .map(([key, value]) => ({ key, length: value.length }));
                
                if (possibleArrays.length > 0) {
                    // Use the largest array found
                    const largestArray = possibleArrays.sort((a, b) => b.length - a.length)[0];
                    //console.log(`[adsApi.js] fetchInvitationsFromApi: Found array in property '${largestArray.key}' with ${largestArray.length} items`);
                    return response.data[largestArray.key];
                }
            }
            
            // If we can't find an array, return empty array to prevent map errors
            console.log(`[adsApi.js] fetchInvitationsFromApi: Could not find an array in response, returning empty array`);
            return [];
        }
        
        return response.data;
    } catch (error) {
        console.error(`[adsApi.js] Error fetching Google Ads invitations from ${fullUrl}: Status: ${error.response?.status}, Code: ${error.code}, Message: ${error.message}, Data:`, error.response?.data ? JSON.stringify(error.response.data) : 'N/A');
        throw error;
    }
};


/**
 * Fetches the list of top-level MCC accounts from the backend API.
 * @returns {Promise<Array>} Promise resolving to an array of account objects.
 */
export const fetchTopLevelAccountsFromApi = async (forceRefresh = false) => {
    // NOTE: This now sources from BigQuery-backed `ads_mcc_accounts` via the new endpoint.
    const endpointPath = '/api/google-ads/ads-mcc-accounts';
    const fullUrl = `${API_BASE_URL}${endpointPath}?_t=${new Date().getTime()}${forceRefresh ? '&force=true' : ''}`;
    //console.log(`[adsApi.js] fetchTopLevelAccountsFromApi: Requesting URL: ${fullUrl}`);
    try {
        const config = await createAxiosConfig();
        const response = await axios.get(fullUrl, config);
        //console.log(`[adsApi.js] fetchTopLevelAccountsFromApi: Response for ${fullUrl} - Status: ${response.status}`);
        return response.data;
    } catch (error) {
        console.error(`[adsApi.js] Error fetching Google Ads top-level accounts from ${fullUrl}: Status: ${error.response?.status}, Code: ${error.code}, Message: ${error.message}, Data:`, error.response?.data ? JSON.stringify(error.response.data) : 'N/A');
        throw error;
    }
};

/**
 * Fetches the list of managed accounts from the backend API.
 * @returns {Promise<Array>} Promise resolving to an array of managed account objects.
 */
export const fetchAdsManagedAccountsFromApi = async (forceRefresh = false) => {
    const endpointPath = '/api/google-ads/ads-managed-accounts';
    const fullUrl = `${API_BASE_URL}${endpointPath}?_t=${new Date().getTime()}${forceRefresh ? '&force=true' : ''}`;
    try {
        const config = await createAxiosConfig();
        const response = await axios.get(fullUrl, config);
        return response.data;
    } catch (error) {
        console.error(`[adsApi.js] Error fetching Google Ads managed accounts from ${fullUrl}: Status: ${error.response?.status}, Code: ${error.code}, Message: ${error.message}, Data:`, error.response?.data ? JSON.stringify(error.response.data) : 'N/A');
        throw error;
    }
};

// Fetch audit logs
export const fetchAuditLogsFromApi = async (forceRefresh = false) => {
    const endpointPath = '/api/google-ads/audit-logs';
    const fullUrl = `${API_BASE_URL}${endpointPath}?_t=${new Date().getTime()}${forceRefresh ? '&force=true' : ''}`;
    //console.log(`[adsApi.js] fetchAuditLogsFromApi: Requesting URL: ${fullUrl}`);
    try {
        const config = await createAxiosConfig();
        const response = await axios.get(fullUrl, config);
        //console.log(`[adsApi.js] fetchAuditLogsFromApi: Response for ${fullUrl} - Status: ${response.status}`);
        return response.data;
    } catch (error) {
        console.error(`[adsApi.js] Error fetching Google Ads audit logs from ${fullUrl}: Status: ${error.response?.status}, Code: ${error.code}, Message: ${error.message}, Data:`, error.response?.data ? JSON.stringify(error.response.data) : 'N/A');
        throw error;
    }
};

// Fetch My Access data for the logged-in user
export const fetchMyAccessFromApi = async (userEmail, forceRefresh = false) => {
    if (!userEmail) {
        console.error('[adsApi.js] fetchMyAccessFromApi: User email is required.');
        throw new Error('User email is required to fetch My Access data');
    }
    const endpointPath = '/api/google-ads/my-access';
    const fullUrl = `${API_BASE_URL}${endpointPath}?email=${encodeURIComponent(userEmail)}&_t=${new Date().getTime()}${forceRefresh ? '&force=true' : ''}`;
    //console.log(`[adsApi.js] fetchMyAccessFromApi: Requesting URL: ${fullUrl}`);
    try {
        const config = await createAxiosConfig();
        const response = await axios.get(fullUrl, config);
        //console.log(`[adsApi.js] fetchMyAccessFromApi: Response for ${fullUrl} - Status: ${response.status}`);
        return response.data;
    } catch (error) {
        console.error(`[adsApi.js] Error fetching My Access data from ${fullUrl}: Status: ${error.response?.status}, Code: ${error.code}, Message: ${error.message}, Data:`, error.response?.data ? JSON.stringify(error.response.data) : 'N/A');
        throw error;
    }
};

// Fetch pending requests from API
export const fetchPendingRequestsFromApi = async (forceRefresh = false) => {
    const endpointPath = '/api/google-ads/get-pending-requests';
    const fullUrl = `${API_BASE_URL}${endpointPath}?_t=${new Date().getTime()}${forceRefresh ? '&force=true' : ''}`;
    //console.log(`[adsApi.js] fetchPendingRequestsFromApi: Requesting URL: ${fullUrl}`);
    try {
        const config = await createAxiosConfig();
        const response = await axios.get(fullUrl, config);
        //console.log(`[adsApi.js] fetchPendingRequestsFromApi: Response for ${fullUrl} - Status: ${response.status}`);
        return response.data;
    } catch (error) {
        console.error(`[adsApi.js] Error fetching Google Ads pending requests from ${fullUrl}: Status: ${error.response?.status}, Code: ${error.code}, Message: ${error.message}, Data:`, error.response?.data ? JSON.stringify(error.response.data) : 'N/A');
        throw error;
    }
};

// Fetch approved requests from API
export const fetchApprovedRequestsFromApi = async (forceRefresh = false) => {
    const endpointPath = '/api/google-ads/get-approved-requests';
    const fullUrl = `${API_BASE_URL}${endpointPath}?_t=${new Date().getTime()}${forceRefresh ? '&force=true' : ''}`;
    //console.log(`[adsApi.js] fetchApprovedRequestsFromApi: Requesting URL: ${fullUrl}`);
    try {
        const config = await createAxiosConfig();
        const response = await axios.get(fullUrl, config);
        //console.log(`[adsApi.js] fetchApprovedRequestsFromApi: Response for ${fullUrl} - Status: ${response.status}`);
        return response.data;
    } catch (error) {
        console.error(`[adsApi.js] Error fetching Google Ads approved requests from ${fullUrl}: Status: ${error.response?.status}, Code: ${error.code}, Message: ${error.message}, Data:`, error.response?.data ? JSON.stringify(error.response.data) : 'N/A');
        throw error;
    }
};

// Fetch rejected requests from API
export const fetchRejectedRequestsFromApi = async (forceRefresh = false) => {
    const endpointPath = '/api/google-ads/get-rejected-requests';
    const fullUrl = `${API_BASE_URL}${endpointPath}?_t=${new Date().getTime()}${forceRefresh ? '&force=true' : ''}`;
    //console.log(`[adsApi.js] fetchRejectedRequestsFromApi: Requesting URL: ${fullUrl}`);
    try {
        const config = await createAxiosConfig();
        const response = await axios.get(fullUrl, config);
        //console.log(`[adsApi.js] fetchRejectedRequestsFromApi: Response for ${fullUrl} - Status: ${response.status}`);
        return response.data;
    } catch (error) {
        console.error(`[adsApi.js] Error fetching Google Ads rejected requests from ${fullUrl}: Status: ${error.response?.status}, Code: ${error.code}, Message: ${error.message}, Data:`, error.response?.data ? JSON.stringify(error.response.data) : 'N/A');
        throw error;
    }
};


/**
 * Modifies a user's access in Google Ads accounts.
 * Can be used for both updating and removing access.
 * 
 * @param {Object} requestData - Request data containing operationType, customerId, userEmail, etc.
 * @returns {Promise<Object>} Promise resolving to the API response data
 */
export const modifyUserAccessInApi = async (requestData) => {
    const endpointPath = '/api/google-ads/access';
    const fullUrl = `${API_BASE_URL}${endpointPath}`;
    //console.log(`[adsApi.js] modifyUserAccessInApi: Requesting URL: ${fullUrl} with payload:`, requestData);
    try {
        const config = await createAxiosConfig({ method: 'POST' });
        const response = await axios.post(fullUrl, requestData, config);
        //console.log(`[adsApi.js] modifyUserAccessInApi: Response for ${fullUrl} - Status: ${response.status}`);
        return response.data;
    } catch (error) {
        console.error(`[adsApi.js] Error modifying user access at ${fullUrl}: Status: ${error.response?.status}, Code: ${error.code}, Message: ${error.message}, Data:`, error.response?.data ? JSON.stringify(error.response.data) : 'N/A');
        throw error;
    }
};


/**
 * Removes a user's invitation to a Google Ads account.
 * 
 * @param {Object} requestData - Request data containing operationType, customerId, userEmail, etc.
 * @returns {Promise<Object>} Promise resolving to the API response data
 */
export const removeInvitationFromApi = async (requestData) => {
    const endpointPath = '/api/google-ads/remove-invitation';
    const fullUrl = `${API_BASE_URL}${endpointPath}`;
    //console.log(`[adsApi.js] removeInvitationFromApi: Requesting URL: ${fullUrl} with payload:`, requestData);
    try {
        const config = await createAxiosConfig({ method: 'POST' });
        const response = await axios.post(fullUrl, requestData, config);
        //console.log(`[adsApi.js] removeInvitationFromApi: Response for ${fullUrl} - Status: ${response.status}`);
        return response.data;
    } catch (error) {
        console.error(`[adsApi.js] Error removing invitation from ${fullUrl}: Status: ${error.response?.status}, Code: ${error.code}, Message: ${error.message}, Data:`, error.response?.data ? JSON.stringify(error.response.data) : 'N/A');
        throw error;
    }
};


/**
 * Approves a user's pending access request to a Google Ads account.
 * @param {Object} requestData - Request data containing operationType, customerId, userEmail, etc.
 * @returns {Promise<Object>} Promise resolving to the API response data
 */
export const approvePendingRequestFromApi = async (requestData) => {
    const endpointPath = '/api/google-ads/approve-pending-request';
    const fullUrl = `${API_BASE_URL}${endpointPath}`;
    //console.log(`[adsApi.js] approvePendingRequestFromApi: Requesting URL: ${fullUrl} with payload:`, requestData);
    try {
        const config = await createAxiosConfig({ method: 'POST' });
        const response = await axios.post(fullUrl, requestData, config);
        //console.log(`[adsApi.js] approvePendingRequestFromApi: Response for ${fullUrl} - Status: ${response.status}`);
        return response.data;
    } catch (error) {
        console.error(`[adsApi.js] Error approving pending request at ${fullUrl}: Status: ${error.response?.status}, Code: ${error.code}, Message: ${error.message}, Data:`, error.response?.data ? JSON.stringify(error.response.data) : 'N/A');
        throw error;
    }
};


/**
 * Reject a user's pending access request to a Google Ads account.
 * @param {Object} requestData - Request data containing operationType, customerId, userEmail, etc.
 * @returns {Promise<Object>} Promise resolving to the API response data
 */
export const rejectPendingRequestFromApi = async (requestData) => {
    const endpointPath = '/api/google-ads/reject-pending-request';
    const fullUrl = `${API_BASE_URL}${endpointPath}`;
    //console.log(`[adsApi.js] approvePendingRequestFromApi: Requesting URL: ${fullUrl} with payload:`, requestData);
    try {
        const config = await createAxiosConfig({ method: 'POST' });
        const response = await axios.post(fullUrl, requestData, config);
        //console.log(`[adsApi.js] approvePendingRequestFromApi: Response for ${fullUrl} - Status: ${response.status}`);
        return response.data;
    } catch (error) {
        console.error(`[adsApi.js] Error approving pending request at ${fullUrl}: Status: ${error.response?.status}, Code: ${error.code}, Message: ${error.message}, Data:`, error.response?.data ? JSON.stringify(error.response.data) : 'N/A');
        throw error;
    }
};


/**
 * Sets the expiration date for a user's access to a Google Ads account.
 * 
 * @param {Object} requestData - Request data containing customerId, userEmail, userId, expirationDays
 * @param {string} requestData.customerId - The Google Ads Customer ID
 * @param {string} requestData.userEmail - The email address of the user
 * @param {string} requestData.userId - The user ID
 * @param {number} requestData.expirationDays - Number of days until access expires
 * @returns {Promise<Object>} Promise resolving to the API response data
 * 
 * Response structure:
 * {
 *   success: "true" | "false",
 *   message: string,
 *   data: object
 * }
 */
export const setAccessExpirationInApi = async (requestData) => {
    const endpointPath = '/api/google-ads/set-access-expiration';
    const fullUrl = `${API_BASE_URL}${endpointPath}`;
    console.log(`[adsApi.js] setAccessExpirationInApi: Requesting URL: ${fullUrl} with payload:`, requestData);
    
    // Validate required fields
    if (!requestData.customerId || !requestData.userEmail || !requestData.userId || !requestData.expirationDays) {
        throw new Error('Missing required fields: customerId, userEmail, userId, and expirationDays are required');
    }
    
    try {
        const config = await createAxiosConfig({ method: 'POST' });
        const response = await axios.post(fullUrl, requestData, config);
        console.log(`[adsApi.js] setAccessExpirationInApi: Response for ${fullUrl} - Status: ${response.status}`);
        return response.data;
    } catch (error) {
        console.error(`[adsApi.js] Error setting access expiration at ${fullUrl}: Status: ${error.response?.status}, Code: ${error.code}, Message: ${error.message}, Data:`, error.response?.data ? JSON.stringify(error.response.data) : 'N/A');
        throw error;
    }
};


/**
 * Creates batch access bindings for multiple Google Ads accounts.
 * This function is used for batch invite operations.
 *
 * @param {Object} requestData - Request data containing:
 *   - operationType: 'batch_invite'
 *   - requestedBy: Email of the requesting user
 *   - requestDetails: Justification text
 *   - batchRequests: Array of request objects with:
 *     - customer_id: Google Ads customer ID
 *     - customer_name: Account name (optional)
 *     - requested_for: Email of user to grant access
 *     - requested_role: Access role (ADMIN, STANDARD, READ_ONLY, EMAIL_ONLY)
 *     - expiration_days: Optional expiration period
 * @returns {Promise<Object>} Promise resolving to the API response data
 *
 * Response structure:
 * {
 *   success: "true" | "false",
 *   statusCode: number,
 *   message: string,
 *   details: {
 *     text: string,
 *     granted?: number,
 *     queued?: number,
 *     failed?: number,
 *     requests?: Array,
 *     errors?: Array
 *   },
 *   error: object
 * }
 */
export const createBatchAccessBindingsFromApi = async (requestData) => {
    const endpointPath = '/api/google-ads/access';
    const fullUrl = `${API_BASE_URL}${endpointPath}`;
    console.log(`[adsApi.js] createBatchAccessBindingsFromApi: Requesting URL: ${fullUrl} with ${requestData.batchRequests?.length || 0} requests`);

    try {
        const config = await createAxiosConfig({
            method: 'POST',
            timeout: 120000 // 2 minute timeout for batch operations
        });
        const response = await axios.post(fullUrl, requestData, config);
        console.log(`[adsApi.js] createBatchAccessBindingsFromApi: Response for ${fullUrl} - Status: ${response.status}`);
        return response.data;
    } catch (error) {
        console.error(`[adsApi.js] Error creating batch access bindings at ${fullUrl}: Status: ${error.response?.status}, Code: ${error.code}, Message: ${error.message}, Data:`, error.response?.data ? JSON.stringify(error.response.data) : 'N/A');
        throw error;
    }
};


/**
 * Checks for duplicate access requests across all three sources:
 * 1. Existing user access (BigQuery all_users_v2 table)
 * 2. Queued access requests (BigQuery pending_access_requests table)
 * 3. Pending invitations (Google Ads API)
 * 
 * This is a comprehensive backend check that works for all users regardless of permissions.
 * 
 * @param {string} email - Email address of the user to check
 * @param {string} customerId - Google Ads customer ID to check against
 * @param {string} requestingUser - Email of the user making the request (optional)
 * @returns {Promise<Object>} Promise resolving to duplicate check result
 * 
 * Response structure:
 * {
 *   success: boolean,
 *   isDuplicate: boolean,
 *   duplicateType: 'existing_access' | 'queued_request' | 'pending_invitation' | null,
 *   details: {
 *     email: string,
 *     customerId: string,
 *     customerName: string,
 *     role: string,
 *     message: string
 *   } | null
 * }
 */
export const checkDuplicateRequestFromApi = async (email, customerId, requestingUser) => {
    const endpointPath = '/api/google-ads/check-duplicate-request';
    const fullUrl = `${API_BASE_URL}${endpointPath}`;
    const payload = { 
        email, 
        customerId,
        requestingUser: requestingUser || "not-allowed@s360digital.com"
    };
    
    //console.log(`[adsApi.js] checkDuplicateRequestFromApi: Requesting URL: ${fullUrl} with payload:`, payload);
    
    try {
        const config = await createAxiosConfig({ method: 'POST' });
        const response = await axios.post(fullUrl, payload, config);
        //console.log(`[adsApi.js] checkDuplicateRequestFromApi: Response for ${fullUrl} - Status: ${response.status}`);
        //console.log(`[adsApi.js] checkDuplicateRequestFromApi: Duplicate check result:`, {
        //    isDuplicate: response.data.isDuplicate,
        //    duplicateType: response.data.duplicateType
        //});
        return response.data;
    } catch (error) {
        console.error(`[adsApi.js] Error checking duplicate request at ${fullUrl}: Status: ${error.response?.status}, Code: ${error.code}, Message: ${error.message}, Data:`, error.response?.data ? JSON.stringify(error.response.data) : 'N/A');
        
        // Return a fail-open response if the check fails
        // This allows the request to proceed if the duplicate check service is down
        //console.warn('[adsApi.js] Duplicate check failed, returning fail-open response (isDuplicate: false)');
        return {
            success: false,
            isDuplicate: false,
            duplicateType: null,
            details: null,
            error: error.message
        };
    }
};
